'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function CreditCardPayoffCalculator() {
  const t = useTranslations("tools.creditCardPayoffCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Calculator state
  const [monthlyBudget, setMonthlyBudget] = useState<number>(500);
  const [cards, setCards] = useState([
    { name: 'Card 1', balance: 4600, minPayment: 100, apr: 18.99 },
    { name: 'Card 2', balance: 3900, minPayment: 90, apr: 19.99 },
    { name: 'Card 3', balance: 6000, minPayment: 120, apr: 15.99 },
  ]);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState<string>('');

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    success: '#10b981',
    successLight: '#f0fdf4',
    error: '#ef4444',
    errorLight: '#fef2f2',
  };

  // Add new card
  const addCard = () => {
    setCards([...cards, { name: `Card ${cards.length + 1}`, balance: 0, minPayment: 0, apr: 0 }]);
  };

  // Remove card
  const removeCard = (index: number) => {
    setCards(cards.filter((_, i) => i !== index));
  };

  // Update card
  const updateCard = (index: number, field: string, value: string | number) => {
    const newCards = [...cards];
    newCards[index] = { ...newCards[index], [field]: value };
    setCards(newCards);
  };

  // Calculate payoff
  const calculatePayoff = (cardsData: any[], budget: number) => {
    const cardsCopy = cardsData.map(c => ({
      ...c,
      originalBalance: c.balance,
      monthlyRate: c.apr / 100 / 12
    }));

    let month = 0;
    let totalInterest = 0;
    const payoffOrder: any[] = [];

    while (cardsCopy.some(c => c.balance > 0) && month < 600) {
      month++;
      let budgetRemaining = budget;

      // Add interest
      cardsCopy.forEach(card => {
        if (card.balance > 0) {
          const interest = card.balance * card.monthlyRate;
          card.balance += interest;
          totalInterest += interest;
        }
      });

      // Make minimum payments
      cardsCopy.forEach(card => {
        if (card.balance > 0 && budgetRemaining > 0) {
          const payment = Math.min(card.minPayment, card.balance, budgetRemaining);
          card.balance -= payment;
          budgetRemaining -= payment;

          if (card.balance < 0.01) {
            card.balance = 0;
            if (!payoffOrder.find(p => p.name === card.name)) {
              payoffOrder.push({
                name: card.name,
                month: month,
                originalBalance: card.originalBalance,
                apr: card.apr
              });
            }
          }
        }
      });

      // Apply extra payment to highest APR card
      const activeCards = cardsCopy.filter(c => c.balance > 0);
      if (activeCards.length > 0 && budgetRemaining > 0) {
        const highestAPRCard = activeCards.sort((a, b) => b.apr - a.apr)[0];
        const extraPayment = Math.min(budgetRemaining, highestAPRCard.balance);
        highestAPRCard.balance -= extraPayment;

        if (highestAPRCard.balance < 0.01) {
          highestAPRCard.balance = 0;
          if (!payoffOrder.find(p => p.name === highestAPRCard.name)) {
            payoffOrder.push({
              name: highestAPRCard.name,
              month: month,
              originalBalance: highestAPRCard.originalBalance,
              apr: highestAPRCard.apr
            });
          }
        }
      }
    }

    const totalDebt = cardsData.reduce((sum, c) => sum + c.balance, 0);

    return {
      totalDebt,
      months: month,
      totalInterest,
      totalPaid: totalDebt + totalInterest,
      payoffOrder
    };
  };

  // Handle form submit
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults(null);

    const validCards = cards.filter(c => c.balance > 0);

    if (validCards.length === 0) {
      setError(t('errorNoCards'));
      return;
    }

    const totalMinPayment = validCards.reduce((sum, c) => sum + c.minPayment, 0);

    if (monthlyBudget < totalMinPayment) {
      setError(t('errorInsufficientBudget', { budget: monthlyBudget.toFixed(2), minPayment: totalMinPayment.toFixed(2) }));
      return;
    }

    const calculatedResults = calculatePayoff(validCards, monthlyBudget);
    setResults(calculatedResults);
  };

  // Styles
  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' },
    { padding: '1.5rem' },
    { maxWidth: '700px', padding: '2rem' },
    { maxWidth: '900px', padding: '2.5rem' }
  );

  const toolHeaderStyle = {
    textAlign: 'center' as const,
    marginBottom: '2rem'
  };

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolDescriptionStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', color: '#334155', fontWeight: 600, marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1.05rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const cardsSectionStyle = getResponsiveStyle(
    {
      marginTop: '2rem',
      background: '#f8fafc',
      borderRadius: '0.75rem',
      border: '2px dashed #cbd5e1'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const cardEntryStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
      border: '1px solid #e2e8f0',
      position: 'relative' as const
    },
    { padding: '1rem' },
    { padding: '1.15rem' },
    { padding: '1.25rem' }
  );

  const cardHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem'
  };

  const cardNumberStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' }
  );

  const removeButtonStyle = getResponsiveStyle(
    {
      background: colors.error,
      color: 'white',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'background 0.3s ease'
    },
    { padding: '0.4rem 0.8rem', fontSize: '0.8rem' },
    { padding: '0.45rem 0.85rem', fontSize: '0.85rem' },
    { padding: '0.5rem 0.9rem', fontSize: '0.9rem' }
  );

  const cardInputsStyle = screenSize === 'mobile'
    ? { display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem' }
    : { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '0.75rem' };

  const cardInputGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const
  };

  const cardLabelStyle = {
    fontSize: '0.8rem',
    color: '#64748b',
    marginBottom: '0.25rem',
    fontWeight: 500
  };

  const cardInputStyle = {
    padding: '0.6rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    fontSize: '0.9rem'
  };

  const addCardButtonStyle = getResponsiveStyle(
    {
      background: colors.success,
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontWeight: 600,
      transition: 'all 0.3s ease',
      marginTop: '1rem'
    },
    { padding: '0.75rem 1.5rem', fontSize: '0.9rem' },
    { padding: '0.8rem 1.6rem', fontSize: '0.95rem' },
    { padding: '0.85rem 1.7rem', fontSize: '1rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1.5rem'
    },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1.05rem', fontSize: '1.05rem' },
    { padding: '1.1rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      marginTop: '2rem',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #10b981'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: '#166534', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const summaryGridStyle = screenSize === 'mobile'
    ? { display: 'grid', gridTemplateColumns: '1fr', gap: '1rem', marginBottom: '2rem' }
    : { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' };

  const summaryCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.5rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    },
    { padding: '1rem' },
    { padding: '1.15rem' },
    { padding: '1.25rem' }
  );

  const summaryLabelStyle = {
    color: '#64748b',
    fontSize: '0.85rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.5rem'
  };

  const summaryValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.75rem' }
  );

  const scheduleTableStyle = {
    width: '100%',
    background: 'white',
    borderRadius: '0.5rem',
    overflow: screenSize === 'mobile' ? 'auto' as const : 'hidden' as const,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const
  };

  const thStyle = getResponsiveStyle(
    {
      background: '#166534',
      color: 'white',
      textAlign: 'left' as const,
      fontWeight: 600
    },
    { padding: '0.5rem', fontSize: '0.85rem' },
    { padding: '0.65rem', fontSize: '0.9rem' },
    { padding: '0.75rem', fontSize: '0.95rem' }
  );

  const tdStyle = getResponsiveStyle(
    {
      borderBottom: '1px solid #e2e8f0',
      color: '#334155'
    },
    { padding: '0.5rem', fontSize: '0.85rem' },
    { padding: '0.65rem', fontSize: '0.9rem' },
    { padding: '0.75rem', fontSize: '0.95rem' }
  );

  const errorStyle = getResponsiveStyle(
    {
      background: colors.errorLight,
      color: colors.error,
      borderRadius: '0.5rem',
      borderLeft: '4px solid #dc2626',
      marginTop: '1rem',
      lineHeight: 1.5
    },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '0.95rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.7, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const listStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.8, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.35rem' },
    { fontSize: '1.5rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const,
    transition: 'color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
            <p style={toolDescriptionStyle}>{t('toolDescription')}</p>
          </div>

          <form onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label htmlFor="monthly-budget" style={labelStyle}>
                {t('monthlyBudgetLabel')}
              </label>
              <input
                type="number"
                id="monthly-budget"
                step="0.01"
                min="0"
                required
                value={monthlyBudget}
                onChange={(e) => setMonthlyBudget(parseFloat(e.target.value) || 0)}
                placeholder="500"
                style={inputStyle}
              />
            </div>

            <div style={cardsSectionStyle}>
              <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.2rem', marginBottom: '1rem' }}>
                {t('yourCardsTitle')}
              </h3>

              {cards.map((card, index) => (
                <div key={index} style={cardEntryStyle}>
                  <div style={cardHeaderStyle}>
                    <span style={cardNumberStyle}>{t('cardNumber', { number: index + 1 })}</span>
                    {cards.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCard(index)}
                        style={removeButtonStyle}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#dc2626'}
                        onMouseLeave={(e) => e.currentTarget.style.background = colors.error}
                      >
                        {t('removeButton')}
                      </button>
                    )}
                  </div>
                  <div style={cardInputsStyle}>
                    <div style={cardInputGroupStyle}>
                      <label style={cardLabelStyle}>{t('cardNameLabel')}</label>
                      <input
                        type="text"
                        value={card.name}
                        onChange={(e) => updateCard(index, 'name', e.target.value)}
                        placeholder={t('cardNamePlaceholder', { number: index + 1 })}
                        style={cardInputStyle}
                      />
                    </div>
                    <div style={cardInputGroupStyle}>
                      <label style={cardLabelStyle}>{t('balanceLabel')}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={card.balance}
                        onChange={(e) => updateCard(index, 'balance', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        required
                        style={cardInputStyle}
                      />
                    </div>
                    <div style={cardInputGroupStyle}>
                      <label style={cardLabelStyle}>{t('minPaymentLabel')}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={card.minPayment}
                        onChange={(e) => updateCard(index, 'minPayment', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        required
                        style={cardInputStyle}
                      />
                    </div>
                    <div style={cardInputGroupStyle}>
                      <label style={cardLabelStyle}>{t('aprLabel')}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={card.apr}
                        onChange={(e) => updateCard(index, 'apr', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        required
                        style={cardInputStyle}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addCard}
                style={addCardButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#059669';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = colors.success;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {t('addCardButton')}
              </button>
            </div>

            <button
              type="submit"
              style={calculateButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(37, 99, 235, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t('calculateButton')}
            </button>
          </form>

          {error && (
            <div style={errorStyle}>
              {error}
            </div>
          )}

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>
                <span>📊</span> {t('resultsTitle')}
              </h3>

              <div style={summaryGridStyle}>
                <div style={summaryCardStyle}>
                  <div style={summaryLabelStyle}>{t('totalDebtLabel')}</div>
                  <div style={summaryValueStyle}>${results.totalDebt.toFixed(2)}</div>
                </div>
                <div style={summaryCardStyle}>
                  <div style={summaryLabelStyle}>{t('payoffTimeLabel')}</div>
                  <div style={summaryValueStyle}>{results.months} {t('monthsUnit')}</div>
                </div>
                <div style={summaryCardStyle}>
                  <div style={summaryLabelStyle}>{t('totalInterestLabel')}</div>
                  <div style={summaryValueStyle}>${results.totalInterest.toFixed(2)}</div>
                </div>
                <div style={summaryCardStyle}>
                  <div style={summaryLabelStyle}>{t('totalPaidLabel')}</div>
                  <div style={summaryValueStyle}>${results.totalPaid.toFixed(2)}</div>
                </div>
              </div>

              <div style={{ marginTop: '2rem' }}>
                <h4 style={{ ...h3Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.2rem', marginTop: 0 }}>
                  {t('payoffScheduleTitle')}
                </h4>
                <div style={scheduleTableStyle}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>{t('orderColumn')}</th>
                        <th style={thStyle}>{t('cardNameColumn')}</th>
                        <th style={thStyle}>{t('originalBalanceColumn')}</th>
                        <th style={thStyle}>{t('aprColumn')}</th>
                        <th style={thStyle}>{t('paidOffByColumn')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.payoffOrder.map((card: any, index: number) => {
                        const years = Math.floor(card.month / 12);
                        const months = card.month % 12;
                        let timeStr = '';
                        if (years > 0) {
                          timeStr = t('yearsMonths', { years, months });
                        } else {
                          timeStr = t('monthsOnly', { months });
                        }

                        return (
                          <tr key={index} style={{ background: 'transparent' }}>
                            <td style={tdStyle}>{index + 1}</td>
                            <td style={tdStyle}><strong>{card.name}</strong></td>
                            <td style={tdStyle}>${card.originalBalance.toFixed(2)}</td>
                            <td style={tdStyle}>{card.apr.toFixed(2)}%</td>
                            <td style={{ ...tdStyle, color: colors.success, fontWeight: 600 }}>
                              {t('monthNumber', { month: card.month })} ({timeStr})
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <h3 style={h3Style}>{t('debtAvalancheTitle')}</h3>
        <p style={paragraphStyle}>{t('debtAvalancheParagraph')}</p>

        <h3 style={h3Style}>{t('compoundInterestTitle')}</h3>
        <p style={paragraphStyle}>{t('compoundInterestParagraph')}</p>

        <h3 style={h3Style}>{t('minimumPaymentsTitle')}</h3>
        <p style={paragraphStyle}>{t('minimumPaymentsParagraph')}</p>

        <h3 style={h3Style}>{t('payoffStrategiesTitle')}</h3>
        <p style={paragraphStyle}>{t('payoffStrategiesParagraph')}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>{t('strategyAvalanche')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('strategySnowball')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('strategyBalance')}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('benefitsTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>{t('benefit1')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('benefit2')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('benefit3')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('benefit4')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('benefit5')}</li>
        </ul>

        <h3 style={h3Style}>{t('bestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice1')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice2')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice3')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice4')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice5')}</li>
        </ul>

        <h3 style={h3Style}>{t('considerationsTitle')}</h3>
        <p style={paragraphStyle}>{t('considerationsParagraph')}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={h3Style}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={h3Style}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={h3Style}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={h3Style}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={h3Style}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>

        <h3 style={h3Style}>{t('faq6Question')}</h3>
        <p style={paragraphStyle}>{t('faq6Answer')}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://financeops.ai/blogs/how-to-use-credit-card-debt-payoff-calculators-to-save-time-and-money-on-your-journey-to-debt-freedom"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('ref1')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://upgradedpoints.com/credit-cards/calculating-credit-card-payoffs/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('ref2')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://www.consolidatedccu.com/blog/credit-card-payoff-calculator-heres-what-you-need-to-know"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('ref3')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://www.bankrate.com/credit-cards/tools/credit-card-payoff-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('ref4')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a
              href="https://www.experian.com/blogs/ask-experian/should-i-pay-off-my-credit-card-debt-immediately-or-over-time/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('ref5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}